import React, { createContext, useContext, useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "./auth-context";
import { supabase } from "@/integrations/supabase/client";
import { checkAdminAccess } from "@/utils/auth/admin";
import { securityLog } from "@/middlewares/security";

interface AdminContextType {
  isAdmin: boolean;
  loading: boolean;
  checkAccess: () => Promise<boolean>;
}

const defaultAdminContext: AdminContextType = {
  isAdmin: false,
  loading: true,
  checkAccess: async () => false,
};

const AdminContext = createContext<AdminContextType>(defaultAdminContext);

export function AdminProvider({ children }: { children: React.ReactNode }) {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const checkAccess = async (): Promise<boolean> => {
    try {
      if (!user) {
        setIsAdmin(false);
        return false;
      }
      
      // Enhanced admin check with security logging
      securityLog('admin_access_check', { userId: user.id }, 'info');
      
      // Check if the user has admin access
      const hasAccess = await checkAdminAccess();
      
      setIsAdmin(hasAccess);
      
      if (hasAccess) {
        securityLog('admin_access_granted', { userId: user.id }, 'info');
      } else {
        securityLog('admin_access_denied', { userId: user.id }, 'warn');
      }
      
      return hasAccess;
    } catch (error) {
      console.error("Error checking admin access:", error);
      securityLog('admin_access_error', { error: String(error) }, 'error');
      setIsAdmin(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      checkAccess();
    } else {
      setIsAdmin(false);
      setLoading(false);
    }
  }, [user]);

  return (
    <AdminContext.Provider value={{ 
      isAdmin, 
      loading, 
      checkAccess
    }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  return context;
}

// Modified hook to use only after router is available
export function useAdminProtectedRoute() {
  const { isAdmin, loading } = useAdmin();
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Removed direct useNavigate call from here
  // Will be used within components that are inside Router
  
  return { isAdmin, loading };
}
